import {
  useEffect,
  useRef,
  useState,
  type PointerEvent,
  type TouchEvent,
} from "react";
import {
  ArrowLeft,
  Keyboard,
  LayoutDashboard,
  Mail,
  Trash2,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ApiErrorResponse } from "@/services/api/client";
import { helpApi } from "@/services/api/help";
import type {
  HelpPair as HelpPairModel,
  HelpZettel,
  HelpZettelDetail,
} from "@/types/help";
import { useAuthStore } from "@/store";
import { useDirectorEnabled } from "@/hooks/useDirectorEnabled";
import { stripDirectorGate } from "@/components/radar/utils/filterBubbles";
import {
  ROLE_LABELS,
  ROLE_PILL_HEX,
  ROLE_SHORT,
  type Role,
} from "@/types/role";
import { HelpPair } from "@/components/help/HelpPair";
import { HelpZettelStatus } from "@/components/help/HelpZettelStatus";
import { useTalkRecorder } from "./useTalkRecorder";
import {
  deleteLocalAudio,
  loadLocalAudio,
  saveLocalAudio,
} from "./useTalkAudioStore";
import {
  enqueueOffline,
  loadOfflineQueue,
  removeOffline,
  type TalkOfflineItem,
} from "./talkOffline";
import { isOfflineZettelId, mergeOfflineZettel } from "./offlineZettel";
import {
  hideUaTalkZettel,
  isUaTalkListVisible,
  loadUaTalkLocalHides,
} from "./uaTalkList";
import { flushOfflineQueue, isTalkRetryable } from "./talkFlush";
import { subscribeHelpPush } from "./talkPush";
import { useTalkT } from "./useTalkT";
import { TalkOrb } from "./TalkOrb";
import { TalkRadarDisc } from "./TalkRadarDisc";
import { TalkRadarFilter } from "./TalkRadarFilter";
import { TalkTtsOrb } from "./TalkTtsOrb";
import { loadSpeaker, saveSpeaker } from "./talkSpeaker";
import { TalkConfirmCard } from "./TalkConfirmCard";
import { TalkRadarAssign } from "./TalkRadarAssign";
import { TalkRadarRead } from "./TalkRadarRead";
import { TalkRadarMessage } from "./TalkRadarMessage";
import { useTalkRadar, type TalkView } from "./useTalkRadar";
import { WaitingCard } from "./WaitingCard";
import { ListZettelCard } from "./ListZettelCard";
import {
  enqueueTalkTts,
  fetchHelpAudioUrl,
  primeTalkPlayback,
  revokeHelpAudioCache,
  stopTalkAudio,
  stopTalkAudioIfSrc,
  unlockTalkPlayback,
} from "./helpAudio";
import {
  isArmedSupport,
  openHelpZettelStream,
  supportIdAtArm,
} from "./talkStream";
import { talkProbe } from "./talkProbe";
import { openTalkStt, type SttResult, type TalkStt } from "./talkSttStream";
import {
  isTranscriptPending,
  pairFromZettel,
  seedZettelDetail,
} from "./talkFormat";

const EMPTY_PAIRS: HelpPairModel[] = [];
const HEARD_CAP_MS = 1500;

function newClientId(): string {
  return crypto.randomUUID();
}

export function TalkWelt() {
  const t = useTalkT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const availableRoles = useAuthStore((s) => s.availableRoles);
  const switchRole = useAuthStore((s) => s.switchRole);
  const fetchAvailableRoles = useAuthStore((s) => s.fetchAvailableRoles);
  const [view, setView] = useState<TalkView>("list");
  const radar = useTalkRadar(view, setView, user?.role);
  const directorOn = useDirectorEnabled();
  const radarBubbles = directorOn
    ? (radar.radarBubbles ?? [])
    : stripDirectorGate(radar.radarBubbles ?? []);
  const [cardBusy, setCardBusy] = useState(false);
  const [clipFlight, setClipFlight] = useState<number | null>(null);
  const [miniId, setMiniId] = useState<string | null>(null);
  const [keyboard, setKeyboard] = useState(() => user?.stt_enabled === false);
  const [draft, setDraft] = useState("");
  const [nag, setNag] = useState(false);
  const [localUrls, setLocalUrls] = useState<Record<string, string>>({});
  const [queue, setQueue] = useState<TalkOfflineItem[]>([]);
  const [uaHidden, setUaHidden] = useState(
    () => new Set(loadUaTalkLocalHides())
  );
  const [blobStretch, setBlobStretch] = useState(false);
  const [splash, setSplash] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [speaker, setSpeaker] = useState(loadSpeaker);
  const [ttsOn, setTtsOn] = useState(false);
  const [listenGen, setListenGen] = useState(0);
  const [heard, setHeard] = useState("");
  const [lang, setLang] = useState(
    user?.talk_language || user?.talk_voice_langs?.[0] || "de"
  );
  const [vvh, setVvh] = useState<string | undefined>();
  const holdClick = useRef(false);
  const holdStarted = useRef(0);
  const liveRef = useRef<{ abort: () => void } | null>(null);
  const sttRef = useRef<TalkStt | null>(null);
  const heardRef = useRef<Promise<SttResult> | null>(null);
  const pendingHeard = useRef("");
  const uploadedHeard = useRef<{ convId: string; messageId: string } | null>(
    null
  );
  const fileGate = useRef<{ go: boolean; posted: boolean } | null>(null);
  const sentRef = useRef(false);
  const attachRef = useRef<{
    cid?: string;
    mid?: string;
    blob?: Blob;
    ms?: number;
  } | null>(null);
  const pressId = useRef("");
  const armedSend = useRef(false);
  const expectTts = useRef(false);
  const playedTts = useRef(new Set<string>());
  const armedSupport = useRef<string | null>(null);
  const pairsRef = useRef(EMPTY_PAIRS);
  const rec = useTalkRecorder();

  const { data: mine } = useQuery({
    queryKey: ["help-me"],
    queryFn: () => helpApi.getMine(),
    refetchInterval: () => (document.hidden ? false : 8000),
  });
  const { data: list } = useQuery({
    queryKey: ["help-my-zettel"],
    queryFn: () => helpApi.getMyZettel(),
    refetchInterval: () => (document.hidden ? false : 8000),
  });
  const convId = mine?.conversation_id || list?.conversation_id;
  const isUa = user?.role === "ultraadmin";
  const serverZettel = list?.zettel ?? [];
  const zettel = mergeOfflineZettel(
    serverZettel,
    queue,
    convId || "",
    user?.role || "teacher"
  );
  const visibleZettel = isUa
    ? zettel.filter((z) => isUaTalkListVisible(z, uaHidden))
    : zettel;
  const oranges = (isUa ? visibleZettel : serverZettel).filter(
    (z) => z.status === "rueckfrage" || z.status === "bitte_bestaetigen"
  );
  const waiting = visibleZettel.filter((z) => z.status === "wartet");
  const orange = isUa
    ? oranges.length
    : (list?.orange_count ?? mine?.orange_count ?? 0);

  const { data: detail, isError: miniError } = useQuery({
    queryKey: ["help-zettel", convId, miniId],
    queryFn: () => helpApi.getZettel(convId!, miniId!),
    enabled: Boolean(
      convId && miniId && view === "mini" && !isOfflineZettelId(miniId || "")
    ),
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey?.[2] === miniId ? previous : undefined,
    refetchInterval: () => (document.hidden ? false : 8000),
  });

  useEffect(() => {
    void fetchAvailableRoles();
  }, [fetchAvailableRoles]);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const apply = () => setVvh(`${Math.round(vv.height)}px`);
    apply();
    vv.addEventListener("resize", apply);
    vv.addEventListener("scroll", apply);
    return () => {
      vv.removeEventListener("resize", apply);
      vv.removeEventListener("scroll", apply);
    };
  }, []);

  useEffect(
    () => () => {
      stopTalkAudio();
      revokeHelpAudioCache();
    },
    []
  );

  useEffect(() => {
    if (
      typeof Notification !== "undefined" &&
      Notification.permission === "granted"
    ) {
      void subscribeHelpPush().catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (view === "list" && convId) {
      void helpApi
        .markAsRead(convId)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ["help-unread-count"] });
        })
        .catch(() => {});
    }
  }, [view, convId, queryClient]);

  useEffect(() => {
    const seenKey = "sv.talk.nagSeen";
    const seen = new Set(
      JSON.parse(sessionStorage.getItem(seenKey) || "[]") as string[]
    );
    const fresh = oranges.filter(
      (z) => !seen.has(`${z.id}:${z.last_activity_at}`)
    );
    if (fresh.length === 0) return;
    fresh.forEach((z) => seen.add(`${z.id}:${z.last_activity_at}`));
    sessionStorage.setItem(seenKey, JSON.stringify([...seen]));
    const title =
      fresh.some((z) => z.status === "bitte_bestaetigen") &&
      !fresh.some((z) => z.status === "rueckfrage")
        ? t("talk_confirm_status")
        : t("talk_followup");
    if (
      typeof Notification !== "undefined" &&
      Notification.permission === "granted"
    ) {
      try {
        void navigator.serviceWorker?.ready
          .then((reg) => {
            void reg.showNotification(title);
          })
          .catch(() => {
            void new Notification(title);
          });
      } catch {
        try {
          void new Notification(title);
        } catch {
          /* ignore */
        }
      }
    } else if (
      typeof Notification !== "undefined" &&
      Notification.permission !== "granted"
    ) {
      setNag(true);
    }
  }, [oranges, t]);

  useEffect(() => {
    if (rec.capturing || rec.phase === "review") {
      setKeyboard(false);
      setDraft("");
      setRoleOpen(false);
    }
    if (!armedSend.current || rec.phase === "holding" || rec.phase === "review")
      return;
    armedSend.current = false;
    liveRef.current?.abort();
    liveRef.current = null;
    rec.setChunkHandler(null);
    rec.setPcmHandler(null);
  }, [rec.phase, rec.capturing, rec.setChunkHandler, rec.setPcmHandler]);

  useEffect(() => {
    talkProbe("boot2");
    return () => {
      liveRef.current?.abort();
      liveRef.current = null;
      sttRef.current?.close();
      sttRef.current = null;
    };
  }, []);

  function primeTalkInput(): void {
    unlockTalkPlayback();
    primeTalkPlayback();
  }

  function armTtsListen(): void {
    if (!speaker || user?.tts_enabled === false) return;
    primeTalkInput();
    playedTts.current.clear();
    armedSupport.current = supportIdAtArm(pairsRef.current);
    expectTts.current = true;
    setListenGen((n) => n + 1);
  }

  useEffect(() => {
    if (!convId || !miniId || isOfflineZettelId(miniId) || view !== "mini")
      return;
    const expect = expectTts.current;
    expectTts.current = false;
    let sawLive = false;
    let playedThis = false;
    let firstEnded = false;
    let morePending = false;
    let morePlayed = false;
    const playMore = (mid: string) => {
      if (!morePending || !firstEnded || morePlayed) return;
      morePlayed = true;
      void fetchHelpAudioUrl(convId, mid, "more").then((more) => {
        if (more)
          enqueueTalkTts(
            `${mid}:more`,
            more,
            () => setTtsOn(false),
            () => setTtsOn(true)
          );
        else setTtsOn(false);
      });
    };
    return openHelpZettelStream(convId, miniId, (data) => {
      if (data.text) invalidate();
      if (data.live) sawLive = true;
      if (isArmedSupport(data.message_id, armedSupport.current)) return;
      if (playedThis || (!expect && !sawLive && !data.live)) return;
      const mid = data.message_id;
      if (data.more && mid) morePending = true;
      if (!data.tts || !mid || !speaker || user?.tts_enabled === false) return;
      playedThis = true;
      playedTts.current.add(mid);
      void fetchHelpAudioUrl(convId, mid).then((url) => {
        if (!url) {
          playedThis = false;
          playedTts.current.delete(mid);
          setTtsOn(false);
          return;
        }
        enqueueTalkTts(
          mid,
          url,
          () => {
            firstEnded = true;
            if (morePending) playMore(mid);
            else setTtsOn(false);
          },
          () => setTtsOn(true)
        );
      });
    });
  }, [convId, miniId, view, speaker, user?.tts_enabled, listenGen]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["help-me"] });
    queryClient.invalidateQueries({ queryKey: ["help-my-zettel"] });
    queryClient.invalidateQueries({ queryKey: ["help-zettel"] });
  };

  const refreshQueue = () => {
    void loadOfflineQueue().then(setQueue);
  };

  useEffect(() => {
    refreshQueue();
  }, []);

  useEffect(() => {
    if (!convId) return;
    const flush = async () => {
      const result = await flushOfflineQueue(
        convId,
        speaker && user?.tts_enabled !== false
      );
      refreshQueue();
      if (result.changed) invalidate();
      if (result.blocked) setView("list");
    };
    const onOnline = () => {
      void flush();
    };
    window.addEventListener("online", onOnline);
    void flush();
    return () => window.removeEventListener("online", onOnline);
  }, [convId, speaker, user?.tts_enabled]);

  async function resolveConvId(): Promise<string> {
    if (convId) return convId;
    const mine = await Promise.race([
      helpApi.getMine(),
      new Promise<never>((_, reject) =>
        window.setTimeout(() => reject(new Error("help_no_conversation")), 4000)
      ),
    ]);
    const id = mine?.conversation_id;
    if (!id) throw new Error("help_no_conversation");
    queryClient.setQueryData(["help-me"], mine);
    return id;
  }

  function seedMiniDetail(
    zettel: HelpZettel,
    opener?: HelpPairModel["opener"]
  ) {
    const cid = zettel.conversation_id || convId;
    if (!cid || !zettel.id) return;
    queryClient.setQueryData<HelpZettelDetail>(
      ["help-zettel", cid, zettel.id],
      (prev) => seedZettelDetail(prev, zettel, opener)
    );
  }

  const sendText = useMutation({
    mutationFn: async (vars: { body: string; clientId: string }) => {
      const cid = await resolveConvId();
      const selected = bindTarget()?.id;
      return helpApi.createZettel(
        cid,
        vars.body,
        vars.clientId,
        selected,
        speaker && user?.tts_enabled !== false
      );
    },
    onMutate: async (vars) => {
      setDraft("");
      if (!convId) return;
      await enqueueOffline({
        id: vars.clientId,
        conversationId: convId,
        clientId: vars.clientId,
        selectedZettelId: bindTarget()?.id,
        text: vars.body,
        createdAt: new Date().toISOString(),
        preview: vars.body.slice(0, 120),
      });
      refreshQueue();
    },
    onSuccess: async (res, vars) => {
      setClipFlight(null);
      if (vars.clientId) await removeOffline(vars.clientId);
      refreshQueue();
      setKeyboard(false);
      if (res?.zettel) {
        seedMiniDetail(res.zettel, res.message);
        setMiniId(res.zettel.id);
        setView("mini");
      }
      armTtsListen();
      invalidate();
    },
    onError: async (err, vars) => {
      setClipFlight(null);
      if (
        err instanceof ApiErrorResponse &&
        (err.detail === "rueckfrage_pick" || err.statusCode === 409)
      ) {
        setView("list");
        return;
      }
      const cid =
        convId ||
        (
          queryClient.getQueryData(["help-me"]) as
            { conversation_id?: string } | undefined
        )?.conversation_id;
      if (!cid || !isTalkRetryable(err)) {
        setDraft((prev) => prev || vars.body);
        return;
      }
      await enqueueOffline({
        id: vars.clientId,
        conversationId: cid,
        clientId: vars.clientId,
        selectedZettelId: bindTarget()?.id,
        text: vars.body,
        createdAt: new Date().toISOString(),
        preview: vars.body.slice(0, 120),
      });
      refreshQueue();
    },
  });

  const sendAudio = useMutation({
    mutationFn: async (vars: {
      blob: Blob;
      clientId: string;
      durationMs?: number;
      transcript?: string;
      live?: { abort: () => void } | null;
      gate?: { go: boolean; posted: boolean; abort?: () => void };
    }) => {
      if (vars.gate && !vars.gate.go) return null;
      const ac = new AbortController();
      if (vars.gate)
        vars.gate.abort = () => {
          vars.live?.abort();
          ac.abort();
        };
      const ms = vars.durationMs ?? heldMs();
      try {
        vars.live?.abort();
        const upload = helpApi.createZettelAudio(
          await resolveConvId(),
          vars.blob,
          vars.clientId,
          bindTarget()?.id,
          ms,
          speaker && user?.tts_enabled !== false,
          ac.signal,
          vars.transcript
        );
        const res = await Promise.race([
          upload,
          new Promise<null>((_, reject) =>
            ac.signal.addEventListener("abort", () =>
              reject(new DOMException("aborted", "AbortError"))
            )
          ),
        ]);
        if (vars.gate && !vars.gate.go) return null;
        if (vars.gate) vars.gate.posted = true;
        if (res?.message) await saveLocalAudio(res.message.id, vars.blob);
        return res;
      } catch (err) {
        if (ac.signal.aborted || (vars.gate && !vars.gate.go)) return null;
        throw err;
      }
    },
    onSuccess: async (res) => {
      setClipFlight(null);
      if (!res) return;
      if (res.message?.id && res.zettel?.conversation_id) {
        uploadedHeard.current = {
          convId: res.zettel.conversation_id,
          messageId: res.message.id,
        };
        flushHeard();
      }
      rec.discard();
      if (res?.zettel) {
        seedMiniDetail(res.zettel, res.message);
        setMiniId(res.zettel.id);
        setView("mini");
      }
      armTtsListen();
      invalidate();
    },
    onError: async (err, vars) => {
      setClipFlight(null);
      const durationMs = vars.durationMs ?? heldMs();
      rec.discard();
      if (
        err instanceof ApiErrorResponse &&
        (err.detail === "rueckfrage_pick" || err.statusCode === 409)
      ) {
        setView("list");
        return;
      }
      const cid =
        convId ||
        (
          queryClient.getQueryData(["help-me"]) as
            { conversation_id?: string } | undefined
        )?.conversation_id;
      if (!cid || !isTalkRetryable(err)) return;
      await enqueueOffline({
        id: vars.clientId,
        conversationId: cid,
        clientId: vars.clientId,
        selectedZettelId: bindTarget()?.id,
        blob: vars.blob,
        createdAt: new Date().toISOString(),
        preview: t("talk_transcript_pending"),
        durationMs,
      });
      await saveLocalAudio(vars.clientId, vars.blob);
      refreshQueue();
    },
  });

  function tryAttach() {
    const job = attachRef.current;
    if (!job?.blob || !job.mid || !job.cid) return;
    attachRef.current = null;
    void saveLocalAudio(job.mid, job.blob);
    void helpApi.attachRecording(job.cid, job.mid, job.blob, job.ms);
  }

  function onTalkTouchStart(e: TouchEvent) {
    e.preventDefault();
    rec.noteTouches(e.touches.length);
  }

  function onTalkTouchEnd(e: TouchEvent) {
    talkProbe(`tend-${e.touches.length}`);
    rec.noteTouches(e.touches.length);
    if (e.touches.length === 0) onTalkRelease();
  }

  function onTalkCancel() {
    rec.noteTouches(0);
    onTalkRelease();
  }

  function onTalkRelease() {
    void rec.flushPcm();
    const stt = sttRef.current;
    sttRef.current = null;
    heardRef.current = stt ? stt.finish() : heardRef.current;
    rec.setPcmHandler(null);
    if (rec.onPointerUp() === false) {
      talkProbe("up-ignored");
      stt?.close();
      return;
    }
    talkProbe("up");
    const generation = pressId.current;
    const audio = rec.pullRecording();
    if (audio && typeof audio.then === "function") {
      void audio.then((blob) => {
        if (!blob || blob.size < 1) return;
        shipBlob(blob, generation);
      });
    }
    if (heldMs() >= 500) setClipFlight(heldMs());
  }

  useEffect(() => {
    if (!rec.pressed && !rec.capturing) return;
    const release = () => onTalkRelease();
    window.addEventListener("pointerup", release);
    window.addEventListener("touchend", release);
    return () => {
      window.removeEventListener("pointerup", release);
      window.removeEventListener("touchend", release);
    };
  }, [rec.pressed, rec.capturing]);

  function flushHeard(): void {
    const text = pendingHeard.current;
    const uploaded = uploadedHeard.current;
    if (!text || !uploaded) return;
    pendingHeard.current = "";
    void helpApi.applyTranscript(uploaded.convId, uploaded.messageId, text);
  }

  function watchHeard(generation: string): void {
    const pending = heardRef.current;
    if (!pending) return;
    void Promise.race([
      pending,
      new Promise<SttResult>((resolve) =>
        window.setTimeout(() => resolve({ text: "", final: false }), HEARD_CAP_MS)
      ),
    ])
      .then((heard) => {
        if (pressId.current !== generation || !heard.final || !heard.text) return;
        setHeard(heard.text);
        pendingHeard.current = heard.text;
        flushHeard();
      })
      .catch(() => {});
  }

  function shipBlob(blob: Blob, generation: string = pressId.current): void {
    if (sentRef.current || pressId.current !== generation) return;
    sentRef.current = true;
    armedSend.current = false;
    talkProbe("upload");
    setView("list");
    watchHeard(generation);
    sendAudio.mutate({
      blob,
      clientId: generation || newClientId(),
      durationMs: heldMs(),
    });
  }

  function commitTalk() {
    if (sentRef.current || !rec.blob) return;
    const live = liveRef.current;
    const blob = rec.blob;
    const ms = heldMs();
    const gate = {
      go: true,
      posted: false,
      abort: undefined as undefined | (() => void),
    };
    fileGate.current = gate;
    sentRef.current = true;
    armedSend.current = false;
    liveRef.current = null;
    rec.setChunkHandler(null);
    watchHeard(pressId.current);
    sendAudio.mutate({
      blob,
      clientId: pressId.current || newClientId(),
      durationMs: ms,
      live,
      gate,
    });
  }

  useEffect(() => {
    if (!rec.blob) return;
    if (attachRef.current) {
      attachRef.current = {
        ...attachRef.current,
        blob: rec.blob,
        ms: heldMs(),
      };
      tryAttach();
    }
    if (
      rec.phase === "review" &&
      armedSend.current &&
      !sentRef.current &&
      rec.blob &&
      !heardRef.current
    ) {
      commitTalk();
    }
  }, [rec.phase, rec.blob, rec.elapsed]);

  function bindTarget(): HelpZettel | undefined {
    if (miniId && !isOfflineZettelId(miniId)) {
      const cur = serverZettel.find((z) => z.id === miniId);
      if (cur?.status === "rueckfrage" || cur?.status === "bitte_bestaetigen")
        return cur;
      if (view === "mini") return undefined;
    }
    if (oranges.length === 1) return oranges[0];
    return undefined;
  }

  function orangeBlocksCreate(): boolean {
    return oranges.length > 0 && bindTarget() == null;
  }

  function heldMs(): number {
    if (!holdStarted.current) return 0;
    return Math.max(0, Date.now() - holdStarted.current);
  }

  function onTalkPress(e: PointerEvent<HTMLButtonElement>) {
    talkProbe("press");
    if (orangeBlocksCreate()) {
      talkProbe("press-orange");
      setView("list");
      return;
    }
    if (rec.capturing || rec.phase === "holding") {
      talkProbe("press-busy");
      return;
    }
    e.currentTarget.classList.add("hot");
    if (rec.phase === "review") rec.discard();
    holdClick.current = true;
    holdStarted.current = Date.now();
    pressId.current = newClientId();
    heardRef.current = null;
    pendingHeard.current = "";
    uploadedHeard.current = null;
    setHeard("");
    sttRef.current?.close();
    const stt =
      user?.stt_enabled === false ? null : openTalkStt(lang, setHeard);
    sttRef.current = stt;
    sentRef.current = false;
    attachRef.current = null;
    armedSend.current = true;
    liveRef.current?.abort();
    liveRef.current = null;
    rec.setChunkHandler(null);
    rec.setPcmHandler(stt ? (pcm) => stt.send(pcm) : null);
    rec.setShip((blob) => shipBlob(blob));
    stopTalkAudio();
    primeTalkInput();
    rec.onPointerDown(e);
    setTtsOn(false);
  }

  function onTalkClick() {
    if (holdClick.current) return;
    if (orangeBlocksCreate()) {
      setView("list");
      return;
    }
    rec.onClick();
  }

  function inboxClick() {
    if (radar.radarRestoreView) {
      radar.closeRadar();
      return;
    }
    const first =
      visibleZettel.find((z) => !isOfflineZettelId(z.id)) ?? visibleZettel[0];
    if (first) {
      setMiniId(first.id);
      setView("mini");
      return;
    }
    setMiniId(null);
    setView("list");
  }

  function overviewClick() {
    if (radar.radarRestoreView) {
      radar.closeRadar();
      return;
    }
    setMiniId(null);
    setView("list");
  }

  const roles = availableRoles ?? [];
  const multi = roles.length > 1;
  const roleHex = ROLE_PILL_HEX[(user?.role as Role) || "teacher"] || "#2563eb";

  const listMini = miniId ? zettel.find((z) => z.id === miniId) : undefined;
  const detailForMini = detail?.zettel?.id === miniId ? detail : undefined;
  const miniZettel = detailForMini?.zettel ?? listMini;
  const listStatus = miniId
    ? serverZettel.find((z) => z.id === miniId)?.status
    : undefined;
  const pairStatus = listStatus ?? miniZettel?.status;
  const miniPairs =
    view === "mini" && miniId
      ? detailForMini?.pairs?.length
        ? detailForMini.pairs
        : listMini
          ? [pairFromZettel(listMini)]
          : EMPTY_PAIRS
      : EMPTY_PAIRS;
  pairsRef.current = miniPairs;
  useEffect(() => {
    if (
      !convId ||
      !miniId ||
      view !== "mini" ||
      !listStatus ||
      !detail?.zettel.status
    )
      return;
    if (listStatus === detail.zettel.status) return;
    void queryClient.invalidateQueries({
      queryKey: ["help-zettel", convId, miniId],
    });
  }, [convId, miniId, view, listStatus, detail?.zettel.status, queryClient]);
  const openerKey = miniPairs.map((p) => p.opener.id).join(",");
  useEffect(() => {
    let live = true;
    const ids = openerKey ? openerKey.split(",") : [];
    const created: string[] = [];
    void Promise.all(
      ids.map(async (id) => [id, await loadLocalAudio(id)] as const)
    ).then((rows) => {
      if (!live) {
        rows.forEach(([, url]) => {
          if (url) {
            stopTalkAudioIfSrc(url);
            URL.revokeObjectURL(url);
          }
        });
        return;
      }
      const next: Record<string, string> = {};
      for (const [id, url] of rows) {
        if (url) {
          next[id] = url;
          created.push(url);
        }
      }
      setLocalUrls((prev) => {
        const same =
          Object.keys(prev).length === Object.keys(next).length &&
          Object.keys(next).every((k) => prev[k] === next[k]);
        return same ? prev : next;
      });
    });
    return () => {
      live = false;
      created.forEach((u) => {
        stopTalkAudioIfSrc(u);
        URL.revokeObjectURL(u);
      });
    };
  }, [openerKey]);

  const liquidI = Math.max(
    0,
    roles.findIndex((r) => r.role === user?.role)
  );

  function pickRole(role: Role) {
    if (radar.radarRestoreView) radar.closeRadar();
    setBlobStretch(true);
    setSplash(true);
    setRoleOpen(false);
    void switchRole(role)
      .then(() => {
        const switched = useAuthStore.getState().user;
        if (role === "engineer" && switched?.needs_school_selection) {
          navigate("/engineer/schools", { replace: true });
        }
      })
      .catch(() => {});
    window.setTimeout(() => setBlobStretch(false), 420);
    window.setTimeout(() => setSplash(false), 550);
  }

  function onRoleChipClick() {
    setKeyboard(false);
    if (roleOpen) {
      setRoleOpen(false);
      return;
    }
    const openIfMulti = (list: { role: string }[]) => {
      if (list.length > 1) setRoleOpen(true);
    };
    if (roles.length > 1) {
      openIfMulti(roles);
      return;
    }
    void fetchAvailableRoles().then(() => {
      openIfMulti(useAuthStore.getState().availableRoles ?? []);
    });
  }

  const roleChip = (
    <button
      type="button"
      className={`talk-role-chip${roleOpen ? " on" : ""}`}
      aria-expanded={roleOpen}
      aria-haspopup="listbox"
      onClick={onRoleChipClick}
    >
      {ROLE_SHORT[(user?.role as Role) || "teacher"] || user?.role}
    </button>
  );

  const rolePicker = roleOpen && roles.length > 1 && (
    <>
      <button
        type="button"
        className="talk-liquid-scrim"
        aria-label={t("talk_role_now_sub")}
        onClick={() => setRoleOpen(false)}
      />
      <div className="talk-liquid-float" role="listbox">
        <div
          className="talk-liquid"
          style={{
            ["--liquid-n" as string]: String(roles.length),
            ["--liquid-i" as string]: String(liquidI),
          }}
        >
          <div className={`talk-liquid-blob ${blobStretch ? "stretch" : ""}`}>
            <span className="shine" />
          </div>
          {splash && (
            <i
              className="talk-splash go"
              style={{
                left: `calc(6px + ${liquidI} * (100% / ${roles.length}) + 50% / ${roles.length})`,
              }}
            />
          )}
          {roles.map(({ role }) => (
            <button
              type="button"
              key={role}
              role="option"
              aria-selected={user?.role === role}
              className={user?.role === role ? "on" : ""}
              onClick={() => pickRole(role)}
            >
              {ROLE_LABELS[role]}
            </button>
          ))}
        </div>
      </div>
    </>
  );

  useEffect(() => {
    const open = (id: string | null) => {
      if (!id) return;
      setMiniId(id);
      setView("mini");
    };
    open(new URLSearchParams(window.location.search).get("zettel"));
    const onMsg = (event: MessageEvent) => {
      const url = String(event.data?.url || "");
      if (event.data?.type !== "talk-open" || !url) return;
      const id = new URL(url, window.location.origin).searchParams.get(
        "zettel"
      );
      open(id);
    };
    navigator.serviceWorker?.addEventListener("message", onMsg);
    return () => navigator.serviceWorker?.removeEventListener("message", onMsg);
  }, []);

  const listedCard = serverZettel.find((z) => z.id === miniId);
  const confirmSource =
    detailForMini?.zettel?.id === miniId ? detailForMini.zettel : listedCard;
  const confirmCard = confirmSource?.confirm_card;
  const showCard =
    view === "mini" &&
    confirmCard &&
    (confirmSource?.status === "bitte_bestaetigen" || confirmCard.ask);

  function deleteZettel(id: string) {
    const target =
      serverZettel.find((z) => z.id === id) ||
      visibleZettel.find((z) => z.id === id);
    const leaveMini = miniId === id;
    const cid = target?.conversation_id || convId;
    if (isUa) {
      setUaHidden(new Set(hideUaTalkZettel(id)));
      if (leaveMini) {
        setView("home");
        setMiniId(null);
      }
      return;
    }
    if (!cid) return;
    void helpApi
      .deactivateZettel(cid, id)
      .then(async () => {
        if (leaveMini) {
          for (const p of miniPairs) await deleteLocalAudio(p.opener.id);
        } else if (target?.opener_id) {
          await deleteLocalAudio(target.opener_id);
        }
        if (leaveMini) {
          setView("home");
          setMiniId(null);
        }
        invalidate();
      })
      .catch(() => {
        invalidate();
      });
  }

  async function decideCard(kind: "confirm" | "discard") {
    if (!convId || !miniId || cardBusy) return;
    setCardBusy(true);
    try {
      if (kind === "confirm") await helpApi.confirmZettel(convId, miniId);
      else await helpApi.discardZettel(convId, miniId);
      await invalidate();
    } finally {
      setCardBusy(false);
    }
  }

  return (
    <div
      className={`talk-welt${keyboard ? " kb" : ""}`}
      style={{ ["--role" as string]: roleHex, ["--talk-vvh" as string]: vvh }}
    >
      <div className="talk-wash" />
      {radar.radarRestoreView === null && (
      <div className="talk-split">
        <div className="talk-top">
          {clipFlight != null && (
            <article className="talk-pair">
              <div className="flex items-start justify-between gap-2">
                <div className="role">{t("talk_ticket")}</div>
                <span className="help-orange">
                  {t("talk_in_flight").toUpperCase()}
                </span>
              </div>
              <div className="talk-clip">
                <span className="talk-clip-bars" aria-hidden>
                  <b />
                  <b />
                  <b />
                  <b />
                  <b />
                </span>
                <span>
                  {t("talk_clip_sent", {
                    time: `0:${String(Math.max(1, Math.round(clipFlight / 1000))).padStart(2, "0")}`,
                  })}
                </span>
              </div>
            </article>
          )}
          {view === "home" &&
            waiting.map((z) => <WaitingCard key={z.id} z={z} />)}
          {view === "list" &&
            (visibleZettel.length === 0 ? (
              <p className="text-sm text-slate-400 p-4">{t("talk_empty")}</p>
            ) : (
              visibleZettel.map((z) =>
                isOfflineZettelId(z.id) ? (
                  <WaitingCard key={z.id} z={z} />
                ) : (
                  <ListZettelCard
                    key={z.id}
                    z={z}
                    onOpen={() => {
                      setMiniId(z.id);
                      setView("mini");
                    }}
                    onDelete={() => deleteZettel(z.id)}
                  />
                )
              )
            ))}
          {view === "mini" && !miniId && (
            <p className="text-sm text-slate-400 p-4">{t("talk_empty")}</p>
          )}
          {view === "mini" && miniId && convId && (
            <>
              <div className="talk-mini-head">
                <div>
                  <small>{t("talk_ticket")}</small>
                  <b>
                    {miniZettel &&
                    isTranscriptPending(
                      miniZettel.transcript_pending,
                      miniZettel.opener_preview,
                      miniZettel.has_audio
                    )
                      ? t("talk_transcript_pending")
                      : miniZettel?.opener_preview || ""}
                  </b>
                </div>
                {pairStatus && <HelpZettelStatus status={pairStatus} />}
              </div>
              {miniError && miniPairs.length === 0 && (
                <p className="text-sm text-red-400 p-4">
                  {t("help_load_error")}
                </p>
              )}
              {miniPairs.map((p) => (
                <HelpPair
                  key={p.opener.id}
                  pair={p}
                  status={pairStatus}
                  conversationId={convId}
                  localPlayUrl={localUrls[p.opener.id]}
                  allowServerPlay
                />
              ))}
              {pairStatus !== "bitte_bestaetigen" &&
                miniPairs.some((p) =>
                  (p.replies || []).some((r) =>
                    /Dienst gerade voll|nichts verstanden|nicht erreichbar/.test(
                      r.body || ""
                    )
                  )
                ) && (
                  <button
                    type="button"
                    className="talk-orb-action send mt-3"
                    onClick={() => {
                      void helpApi
                        .retryZettel(
                          convId,
                          miniId,
                          speaker && user?.tts_enabled !== false
                        )
                        .then(() => {
                          armTtsListen();
                          invalidate();
                        });
                    }}
                  >
                    {t("talk_transcript_retry")}
                  </button>
                )}
            </>
          )}
          {view === "mini" && miniId && convId && (
            <button
              type="button"
              className="talk-icon-btn talk-trash-btn mt-4"
              aria-label={t("delete")}
              onClick={() => deleteZettel(miniId)}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="talk-mic">
          <div className="talk-pill-glow" />
          {heard && (rec.phase === "holding" || rec.phase === "review") && (
            <div className="talk-timer">{heard}</div>
          )}
          {rec.showTimer && rec.phase === "holding" && !heard && (
            <div className="talk-timer">{Math.floor(rec.elapsed / 1000)}s</div>
          )}
          {ttsOn ? (
            <TalkTtsOrb
              label={t("talk_hold")}
              onPointerDown={
                user?.stt_enabled === false ? undefined : onTalkPress
              }
              onPointerUp={
                user?.stt_enabled === false ? undefined : onTalkRelease
              }
            />
          ) : keyboard || user?.stt_enabled === false ? (
            <form
              className="talk-composer"
              onSubmit={(e) => {
                e.preventDefault();
                primeTalkInput();
                if (orangeBlocksCreate()) {
                  setView("list");
                  return;
                }
                const text = draft.trim();
                if (text)
                  sendText.mutate({ body: text, clientId: newClientId() });
              }}
            >
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    primeTalkInput();
                    e.currentTarget.form?.requestSubmit();
                  }
                }}
                rows={3}
                placeholder={t("talk_keyboard_placeholder")}
              />
              <button
                type="submit"
                className="talk-orb-action send mt-2"
                onPointerDown={() => {
                  primeTalkInput();
                }}
              >
                {t("talk_send")}
              </button>
            </form>
          ) : (
            <TalkOrb
              hot={rec.capturing}
              linking={rec.pressed && !rec.capturing && rec.phase !== "denied"}
              dead={rec.phase === "denied"}
              label={t("talk_hold")}
              hint={t("talk_hold_short")}
              onPointerDown={onTalkPress}
              onPointerUp={onTalkRelease}
              onPointerCancel={onTalkCancel}
              onLostPointerCapture={onTalkCancel}
              onTouchStart={onTalkTouchStart}
              onTouchEnd={onTalkTouchEnd}
              onClick={onTalkClick}
            />
          )}
          {rec.phase !== "holding" &&
            rec.phase !== "review" &&
            !keyboard &&
            !multi && (
              <div className="talk-hold-label">
                {rec.pressed && !rec.capturing && rec.phase !== "denied"
                  ? t("talk_linking")
                  : t("talk_hold")}
              </div>
            )}
          {rec.phase === "denied" && (
            <p className="absolute top-3 text-[11px] text-amber-300 px-4 text-center z-3">
              {t(
                rec.block === "insecure"
                  ? "talk_mic_insecure"
                  : "talk_mic_denied"
              )}
            </p>
          )}
          {multi && !keyboard && rec.phase === "idle" && (
            <div className="talk-role-now">
              <b>{ROLE_LABELS[(user?.role as Role) || "teacher"]}</b>
              <small>{t("talk_role_now_sub")}</small>
            </div>
          )}
          <div className="talk-dock">
            <div />
            <div />
            <button
              type="button"
              className="talk-icon-btn"
              onPointerDown={
                keyboard && user?.stt_enabled !== false
                  ? (e) => {
                      holdClick.current = false;
                      onTalkPress(e);
                    }
                  : undefined
              }
              onPointerUp={
                keyboard && user?.stt_enabled !== false
                  ? onTalkRelease
                  : undefined
              }
              onClick={() => {
                if (
                  holdClick.current &&
                  Date.now() - holdStarted.current > 150
                ) {
                  holdClick.current = false;
                  return;
                }
                if (keyboard) {
                  if (rec.phase === "idle" || rec.phase === "denied") {
                    setKeyboard(false);
                    setDraft("");
                  }
                } else {
                  setRoleOpen(false);
                  setKeyboard(true);
                }
              }}
            >
              {keyboard ? (
                <TalkOrb
                  hot={rec.pressed || rec.capturing}
                  dead={rec.phase === "denied"}
                  scale={0.28}
                  label={t("talk_hold")}
                  visual
                />
              ) : (
                <Keyboard className="w-4 h-4 mx-auto" />
              )}
            </button>
          </div>
        </div>
      </div>
      )}
      {radar.radarRestoreView !== null && (
        <div className="talk-radar" role="region" aria-label={t("talk_radar")}>
          {radar.radarRead === null && (
            <TalkRadarDisc
              bubbles={directorOn ? radar.discBubbles : stripDirectorGate(radar.discBubbles)}
              allBubbles={radarBubbles}
              onOpen={radar.openRadarDot}
              teacherCodes={user?.role === "admin"}
            />
          )}
          {radar.radarRead &&
            !radar.radarMessage &&
            !radar.radarRead.assignLessonId &&
            (user?.role === "admin" || user?.role === "teacher") && (
              <TalkRadarRead
                role={user.role}
                stage={radar.radarRead.stage}
                requestIds={radar.radarRead.requestIds}
                requestId={radar.radarRead.requestId}
                kind={radar.radarRead.kind}
                requestStatus={radar.radarRead.requestStatus}
                requestDate={radar.radarRead.requestDate}
                incoming={radar.radarRead.incoming}
                onOpen={radar.openRadarRequest}
                onAssignLesson={
                  user.role === "admin" ? radar.openAssign : undefined
                }
                onStornoBack={radar.setStornoBack}
                onEdgeBack={radar.radarBack}
                onMessage={() => radar.setRadarMessage(true)}
                bubbles={radar.discBubbles}
                personId={radar.radarRead.personId}
              />
            )}
          {radar.radarMessage && radar.radarRead?.requestId && (
            <TalkRadarMessage
              requestId={radar.radarRead.requestId}
              teachersDeclined={radar.teachersDeclined}
              teachersPending={radar.teachersPending}
              onSent={() => radar.setRadarMessage(false)}
            />
          )}
          {radar.radarRead?.assignLessonId &&
            radar.radarRead.requestId &&
            user?.role === "admin" &&
            !radar.radarMessage && (
              <TalkRadarAssign
                requestId={radar.radarRead.requestId}
                lessonId={radar.radarRead.assignLessonId}
                assignedTeacherName={radar.radarRead.assignSubstitute}
                onAssigned={radar.radarBack}
              />
            )}
          {radar.radarError && (
            <p className="talk-radar-error">{radar.radarError}</p>
          )}
        </div>
      )}
      {radar.radarRestoreView !== null &&
        !radar.radarMessage &&
        !radar.radarRead?.assignLessonId &&
        (radar.radarRead === null ||
          radar.radarRead.stage === "list" ||
          radar.radarRead.stage === "deck") &&
        (user?.role === "admin" || user?.role === "teacher") && (
        <TalkRadarFilter
          role={user.role}
          bubbles={radarBubbles}
          onVisibleIds={radar.setRadarVisibleIds}
          onTimeDays={radar.setRadarTimeDays}
        />
      )}
      <div className="talk-top-nav">
        {radar.radarRestoreView !== null && (
          <button
            type="button"
            className="talk-nav-btn"
            aria-label={t("talk_ask_back")}
            onClick={radar.radarBack}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        {(user?.talk_voice_langs?.length || 0) > 1 && (
          <select
            className="talk-lang"
            value={lang}
            aria-label={t("talk_language")}
            onChange={(e) => {
              const v = e.target.value;
              setLang(v);
              void helpApi.setLanguage(v).catch(() => {});
            }}
          >
            {(user?.talk_voice_langs || ["de"]).map((code) => (
              <option key={code} value={code}>
                {code.toUpperCase()}
              </option>
            ))}
          </select>
        )}
        {user?.tts_enabled !== false && (
          <button
            type="button"
            className={`talk-nav-btn${speaker ? " on" : ""}`}
            aria-label={t("talk_speaker")}
            aria-pressed={speaker}
            onClick={() => {
              const next = !speaker;
              setSpeaker(next);
              saveSpeaker(next);
              if (next) primeTalkInput();
              if (!next) {
                stopTalkAudio();
                setTtsOn(false);
              }
            }}
          >
            {speaker ? (
              <Volume2 className="w-5 h-5" />
            ) : (
              <VolumeX className="w-5 h-5" />
            )}
          </button>
        )}
        <button
          type="button"
          className={`talk-nav-btn${view === "list" ? " on" : ""}`}
          onClick={overviewClick}
          aria-label={t("talk_overview")}
          aria-pressed={view === "list"}
        >
          <LayoutDashboard className="w-5 h-5" />
        </button>
        {(user?.role === "admin" || user?.role === "teacher") && (
          <button
            type="button"
            className={`talk-nav-btn${radar.radarRestoreView ? " on" : ""}`}
            aria-label={t("talk_radar")}
            aria-pressed={radar.radarRestoreView !== null}
            onClick={() =>
              radar.radarRestoreView ? radar.closeRadar() : radar.openRadar()
            }
          >
            <span
              className="talk-sweep-icon"
              role="img"
              aria-label={t("talk_radar_sweep")}
            >
              <i />
              <b />
            </span>
          </button>
        )}
        <button
          type="button"
          className={`talk-nav-btn talk-inbox${view === "mini" ? " on" : ""}`}
          onClick={inboxClick}
          aria-label={t("talk_inbox")}
          aria-pressed={view === "mini"}
        >
          <Mail className="w-5 h-5" />
          {orange > 0 && (
            <span className="talk-inbox-pip">
              {orange > 99 ? "99+" : orange}
            </span>
          )}
        </button>
      </div>
      {roleChip}
      {rolePicker}
      {nag && (
        <div className="talk-nag bg-amber-950 text-amber-100 text-sm rounded-xl p-3">
          <p>{t("talk_push_nag")}</p>
          <button
            type="button"
            className="mt-2 font-bold min-h-11"
            onClick={() => {
              void Notification.requestPermission().then((p) => {
                if (p === "granted") void subscribeHelpPush().catch(() => {});
              });
              setNag(false);
            }}
          >
            {t("talk_push_allow")}
          </button>
        </div>
      )}
      {showCard && confirmCard ? (
        <TalkConfirmCard
          card={confirmCard}
          spoken={false}
          busy={cardBusy}
          onSend={() => {
            void decideCard("confirm");
          }}
          onDiscard={() => {
            void decideCard("discard");
          }}
          onPick={(text) => {
            sendText.mutate({ body: text, clientId: newClientId() });
          }}
        />
      ) : null}
    </div>
  );
}
